import { Dimension } from '@pubstack/common/src/analytics/dimension'
import { MappedName, TopRevenuesByDim } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { FunctionComponent } from 'react'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import Button from '~/components/Button'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { PureTopRevenueByDim } from '~/modules/analytics/common/components/topRevenueByDim/PureTopRevenueByDim'
import {
  getIconFromDimension,
  getTitleFromDimension,
  getUriForMoreFromDimensionServerSide,
  OverviewRevenueChartProps,
} from '~/modules/analytics/common/components/topRevenueByDim/PureTopRevenueByDim.utils'
import { BREAKDOWN_DIMENSIONS } from '~/modules/analytics/server-side/ServerSidePage'
import { Context } from '~/state'
import { WithClassName } from '~/types/utils'
import { RevenueContainer } from '../common/components/topRevenueByDim/container'

const OverviewRevenueChart: FunctionComponent<OverviewRevenueChartProps> = ({ icon, title, context, dimensions, currencySymbol, dimension, revenueDisplayThreshold, onRevenueChartSelect, onMore }) => {
  const analyticsQueryTime = useAnalyticsQueryTanstack<TopRevenuesByDim>('server-side.top.revenues.by.dim', dimensions, context, { dimension })
  const revenueEmptyData: TopRevenuesByDim = { impressionCpmSum: [], adxImpressionCpmSum: [], openBiddingImpressionCpmSum: [], serverSideImpressionCpmSum: [], name: [], total: {} }

  return (
    <PureTopRevenueByDim
      revenueDisplayThreshold={revenueDisplayThreshold}
      onRefreshClick={() => {
        analyticsQueryTime.refetch()
      }}
      icon={icon}
      rawData={analyticsQueryTime.data ?? revenueEmptyData}
      isLoading={analyticsQueryTime.isFetching}
      error={analyticsQueryTime.isError}
      empty={!analyticsQueryTime.isFetching && !analyticsQueryTime?.data?.name.length}
      title={title}
      currencySymbol={currencySymbol}
      onValueSelect={onRevenueChartSelect}
      info={
        onMore && (
          <Button variant={'tertiary'} onClick={() => onMore(dimension)} trackingLabel={`ana-server-side-overview-${dimension}-navigate`}>
            More
          </Button>
        )
      }
    />
  )
}

type OverviewRevenueProps = WithClassName & {
  context: Context
  currencySymbol: CurrencySymbol
  onRevenueChartSelect: (name: MappedName, dimension: Dimension) => void
  onMoreClick: (dimension: Dimension) => void
}
export const ServerSideTopRevenue: FunctionComponent<OverviewRevenueProps> = ({ className, context, currencySymbol, onRevenueChartSelect, onMoreClick }) => {
  const dimensionsToDisplay: Dimension[] = ['bidder', 'tagId', 'country', 'adUnit', 'size']
  const [showMinorData] = useLocalStorage({ key: 'showMinorData', defaultValue: false })
  const revenueDisplayThreshold = showMinorData ? Number.MIN_SAFE_INTEGER : 0.01

  return (
    <RevenueContainer className={className}>
      {dimensionsToDisplay.map((dimension, index) => (
        <OverviewRevenueChart
          key={index}
          title={getTitleFromDimension(dimension)}
          icon={getIconFromDimension(dimension)}
          revenueDisplayThreshold={revenueDisplayThreshold}
          dimension={dimension}
          context={context}
          dimensions={dimensionsToDisplay}
          currencySymbol={currencySymbol}
          moreUri={getUriForMoreFromDimensionServerSide(dimension)}
          onMore={(BREAKDOWN_DIMENSIONS as Dimension[]).includes(dimension) ? onMoreClick : undefined}
          onRevenueChartSelect={(name) => onRevenueChartSelect && onRevenueChartSelect(name, dimension)}
        />
      ))}
    </RevenueContainer>
  )
}
